import { useMutation, useSubscription } from "@apollo/client";
import gql from "graphql-tag";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toastErrorOptions, toastSuccessOptions } from "../../../App";
import { EditableInformation } from "../../SharedComponents/EditableInformation/EditableInformation";
import { ListLoading } from "../../SharedComponents/ListLoading/ListLoading";
import { Select } from "../../SharedComponents/Select/Select";
import { SelectOption } from "../../SharedComponents/Select/SelectOption/SelectOption";
import { UserInvitation } from "../../SharedComponents/UserInvitation/UserInvitation";
import { AdminRemoveUserFromAccountModal } from "../AdminRemoveUserFromAccountModal/AdminRemoveUserFromAccountModal";
import { AdminAccessControlActions } from "./AdminAccessControlActions/AdminAccessControlActions";
import "./AdminUsersControlList.css";

export const getUsers = gql`
  query users($accountIds: [String]) {
    users(accountIds: $accountIds) {
      id
      email
      name
      status
      roles
    }
  }
`;

export const AdminUsersControlList = ({
  accountId,
  selectOptions,
  title = "sectionTitles.administrativeUsers",
  canManage,
  loading,
  error,
  data,
}) => {
  const { t } = useTranslation();
  useSubscription(subscribeToUserSuspended, {
    variables: { accountId },
    onSubscriptionData: ({ client: { cache }, subscriptionData }) => {
      const prev = cache.readQuery({
        query: getUsers,
        variables: { accountIds: [accountId] },
      });

      cache.writeQuery({
        query: getUsers,
        variables: { accountIds: [accountId] },
        data: {
          ...prev,
          users: prev.users.filter(
            (x) =>
              x.id !== subscriptionData.data.onSSOUserRemovedFromAccount.userId
          ),
        },
      });
    },
  });

  const [updateSSOAccountUser] = useMutation(updateSSOUserRoleMutation, {
    update: (cache, { data: { updateSSOUserRole } }) => {
      const prev = cache.readQuery({
        query: getUsers,
        variables: { accountIds: [accountId] },
      });

      const newData = {
        ...prev,
        users: prev.users.map((x) => {
          if (x.id === updateSSOUserRole.id) {
            return {
              ...x,
              ...updateSSOUserRole,
              roles: updateSSOUserRole.roles,
            };
          } else {
            return x;
          }
        }),
      };

      cache.writeQuery({
        query: getUsers,
        variables: { accountIds: [accountId] },
        data: newData,
      });
    },
  });

  const handleRoleChange = (id, newRole) => {
    updateSSOAccountUser({
      variables: { accountId, userId: id, role: newRole },
    })
      .then(() => {
        toast(`${t("toasts.roleChanged")}`, toastSuccessOptions);
      })
      .catch((err) => {
        toast(err.message, toastErrorOptions);
      });
  };

  return (
    <div className="AdminUsersControlList">
      <div className="AdminUsersControlList__add-user">
        <UserInvitation accountId={accountId} />
      </div>
      <EditableInformation title={title}>
        {loading && <ListLoading />}
        {!error &&
          !loading &&
          data?.users?.map((user) => {
            const ssoRole = user?.roles?.sso?.find(
              (x) => x.accountId === accountId
            );

            const actionType = !ssoRole?.accepted ? "cancel" : "revoke";

            const usersAccountStatus = !ssoRole?.accepted
              ? "PENDING"
              : "ACTIVE";

            return (
              <AdminUsersControlListItem
                id={user.id}
                key={user.id}
                name={user.name}
                email={user.email}
                role={ssoRole && ssoRole.role ? ssoRole.role : "USER"}
                setRole={handleRoleChange}
                actionType={actionType}
                accountId={accountId}
                status={usersAccountStatus}
                selectOptions={selectOptions}
                canManage={canManage}
              />
            );
          })}
        {data?.users?.length === 0 && (
          <div className="ApplicationDisplay__no-apps">
            {t("accounts.noUsers")}
          </div>
        )}
      </EditableInformation>
    </div>
  );
};

const updateSSOUserRoleMutation = gql`
  mutation updateSSOUserRole(
    $accountId: String
    $userId: String
    $role: String
  ) {
    updateSSOUserRole(accountId: $accountId, userId: $userId, role: $role) {
      id
      email
      roles
      name
    }
  }
`;

const AdminUsersControlListItem = ({
  id,
  name = "Ukendt navn",
  email,
  role = "EMPLOYEE",
  setRole,
  actionType,
  accountId,
  status,
  selectOptions,
  canManage,
}) => {
  const { t } = useTranslation();
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [removeUser] = useMutation(removeUserMutation);
  return (
    <div className="AdminUsersControlListItem">
      {showRemoveUserModal && (
        <AdminRemoveUserFromAccountModal
          accept={() => {
            removeUser({ variables: { userId: id, accountId } });
            setShowRemoveUserModal(false);
          }}
          reject={() => setShowRemoveUserModal(false)}
          email={email}
        />
      )}
      <div
        className="AdminUsersControlListItem__indicatior"
        style={{ backgroundColor: status === "ACTIVE" ? "#56B267" : "#EAC75A" }}
      ></div>
      {status === "ACTIVE" && (
        <div className="AdminUsersControlListItem__name">{name}</div>
      )}
      {status === "PENDING" && (
        <div className="AdminUsersControlListItem__email">{email}</div>
      )}
      {status === "ACTIVE" && (
        <div className="AdminUsersControlListItem__email">({email})</div>
      )}
      {status === "PENDING" && (
        <div className="AdminUsersControlListItem__pending-invite">
          {t("users.invitePending")}
        </div>
      )}
      {canManage && (
        <div className="AdminUsersControlListItem__role">
          <Select
            change={(role) => setRole(id, role)}
            value={role}
            type="default"
          >
            {selectOptions.map(({ label, value }) => (
              <SelectOption key={value} value={value}>
                {t(label)}
              </SelectOption>
            ))}
          </Select>
        </div>
      )}
      {canManage && (
        <div className="AdminUsersControlListItem__action">
          <AdminAccessControlActions
            type={actionType}
            doAction={() => setShowRemoveUserModal(!showRemoveUserModal)}
          />
        </div>
      )}
    </div>
  );
};

const subscribeToUserSuspended = gql`
  subscription onSSOUserRemovedFromAccount($accountId: String) {
    onSSOUserRemovedFromAccount(accountId: $accountId)
  }
`;

const removeUserMutation = gql`
  mutation removeSSOUserFromAccount($userId: String, $accountId: String) {
    removeSSOUserFromAccount(userId: $userId, accountId: $accountId)
  }
`;
